/**
 * Payment TanStack mutations.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@speakai/ui/toast";
import { apiClient } from "@/lib/api/client";
import {
  analyticsKeys,
  dashboardKeys,
  offerKeys,
  paymentKeys,
  subscriptionKeys,
} from "@/queries/keys";
import { toastError } from "@/lib/integrations/sentry-tracking";
import * as paymentApi from "./api";
import { usePaddle } from "./payment/paddle";
import { confirmStripeCardPayment } from "./payment/stripe";
import type {
  SourceDetails,
  CreateSubscriptionData,
  AutoReloadConfig,
  PaymentSourceType,
} from "./types";
import type {
  TaxInfo,
  BillingAddress,
  BillingAddressPayload,
} from "./payment-queries";
import { refreshPaymentSources } from "./payment-queries";

// -- Subscription Mutations -----------------------------------------

/**
 * Retry a pending subscription payment via Stripe. Each call fetches a fresh
 * one-time clientSecret tied to the current pending intent — do NOT cache via
 * `useQuery`. Caller must ensure a card is on file (open AddSource first if
 * `isPaymentMethod === false` or sources are empty) and owns the post-success
 * refetch.
 */
export function useRetrySubscriptionStripe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await paymentApi.getSubscriptionIntent();
      if (!res.success || !res.data?.clientSecret) {
        throw new Error(res.error ?? "Failed to retrieve payment intent");
      }
      await confirmStripeCardPayment(res.data.clientSecret);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast.
  });
}

/**
 * Retry a pending subscription payment via Paddle. Resolves on
 * `checkout.completed`; user-cancellation throws `Error("checkout_closed")`
 * so the caller can suppress the failure toast.
 */
export function useRetrySubscriptionPaddle() {
  const queryClient = useQueryClient();
  const { openCheckoutAndWait } = usePaddle();

  return useMutation({
    mutationFn: async () => {
      const res = await paymentApi.getPaddleTransaction();
      if (!res.success || !res.data?.id) {
        throw new Error(res.error ?? "Failed to retrieve Paddle transaction");
      }
      await openCheckoutAndWait(res.data.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
  });
}

/**
 * Claim an offer and obtain a provider-hosted checkout URL. The caller performs
 * the full-page redirect to `url`. Error is handled at the call site
 * (mutateAsync + try/catch) — no onError toast to avoid double-toasting.
 */
export function useClaimOffer() {
  return useMutation({
    mutationFn: async (data: {
      offerId: string;
      provider: "stripe" | "paddle";
      token?: string;
    }) => {
      const res = await paymentApi.claimOffer(data);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error ?? "Failed to claim offer");
      }
      return res.data.url;
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      companyId: string;
      reason?: string;
      questions?: { question: string; answer: string }[];
      isOfferAccepted?: boolean;
      offer?: { label: string; offer: string }[];
      feedback?: string;
    }) => {
      const res = await paymentApi.cancelPlan(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to cancel subscription");
      }
      return res;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function usePauseSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      companyId: string;
      duration: number;
      reason?: string;
      questions?: { question: string; answer: string }[];
    }) => {
      const res = await paymentApi.pausePlan(data);
      if (!res.success) throw new Error(res.error ?? "Failed to pause subscription");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function useAcceptOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      companyId: string;
      reason?: string;
      offer?: { label: string; offer: string }[];
      feedback?: string;
    }) => {
      const res = await paymentApi.acceptOfferPlan(data);
      if (!res.success) throw new Error(res.error ?? "Failed to accept offer");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function usePayAsYouGo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => paymentApi.payAsYouGoPlan(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
      // dashboardKeys invalidation is caller-owned so the trial-expired hero
      // can dwell post-success before the dashboard refetch unmounts it.
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function useStripePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      paymentMethodId: string;
      planData: CreateSubscriptionData;
    }) => paymentApi.stripePlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function usePaddlePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      transactionId?: string;
      planData: CreateSubscriptionData;
    }) => paymentApi.paddlePlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

/**
 * RevenueCat plan mutation. Server expects a flat plan body plus a nested
 * `revenuecat: { customerId, productIdentifier, transactionId, storeType, isActive }`
 * object (see api.ts `revenuecatPlan` and subscriptionController.createRevenuecatPlan).
 *
 * NOTE: As of this writing this hook has no caller in the React UI — the
 * RevenueCat in-app purchase callback (iOS/Android via Capacitor) still needs
 * to be wired up. When that flow is added, populate the `revenuecat` fields
 * from the RevenueCat SDK `MakePurchaseResult` / `customerInfo` payload.
 */
export function useRevenuecatPlan() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: {
      planData: CreateSubscriptionData;
      revenuecat: {
        customerId: string;
        productIdentifier: string;
        transactionId: string;
        storeType: string;
        isActive: boolean;
      };
    }) => paymentApi.revenuecatPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
    onError: (err) => {
      toastError(toast, "subscription", "revenuecat_plan_fail", "Something went wrong. Please try again.", { error_reason: String(err).slice(0, 200) });
    },
  });
}

// -- Payment Method Mutations ---------------------------------------

export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { sourceId: string; isDefault?: boolean }) => {
      // apiClient returns { success:false } instead of throwing — throw so onError
      // fires with the real API message instead of a silent set-default no-op.
      const res = await paymentApi.updateSource(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to update payment method");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      toastError(toast, "payment", "update_source_fail", msg, { error_reason: msg });
    },
  });
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: {
      paymentSource: PaymentSourceType;
      sourceId?: string;
    }) => {
      // apiClient returns { success:false } (does NOT throw) on a failed response, e.g.
      // "Paddle payment method cannot be removed while subscription is active". Throw so
      // react-query routes it to onError instead of treating a failed delete as success.
      const res = await paymentApi.deleteSource(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to remove payment method");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void refreshPaymentSources();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      toastError(toast, "payment", "delete_source_fail", msg, { error_reason: msg });
    },
  });
}

export function useSaveSetupIntent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { setupIntentId: string; paymentMethodId?: string }) =>
      paymentApi.saveSetupIntent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void refreshPaymentSources();
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

// -- Balance Mutations ----------------------------------------------

export function useAddBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { paymentIntentId: string }) => paymentApi.addBalanceToAccount(data),
    onSuccess: () => {
      // Invalidate the full payment key tree so auto-reload config and sources
      // are also refreshed — a top-up may change server-side auto-reload state.
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      // In credit mode the wallet is rendered from the subscription/usage endpoints,
      // not paymentKeys.balance() — invalidate those or the pill stays stale.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.usage() });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-notify
  });
}

export function useCreateChargeByBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SourceDetails) => paymentApi.createChargeByBalance(data),
    onSuccess: () => {
      // Invalidate all payment keys (not just balance) because a balance charge
      // can trigger server-side card-status reconciliation that changes sources too.
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
    // with the caller toasts in payWithBalance (use-payment.ts:115-123).
  });
}

export function useCreatePaymentIntent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SourceDetails) => paymentApi.createPaymentIntent(data),
    onSuccess: () => {
      // Invalidate balance after a successful payment intent so the cost-review
      // screen reflects the topped-up balance without waiting for staleTime expiry.
      queryClient.invalidateQueries({ queryKey: paymentKeys.balance() });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function useCreateTopupIntent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SourceDetails) => paymentApi.createTopupIntent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.balance() });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

export function useCreateChargeByPaddle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { amount: number; description: string }) =>
      paymentApi.createChargeByPaddle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
    // error handled at call site (mutateAsync + try/catch) — onError would double-toast
  });
}

// -- Auto-Reload Mutations ------------------------------------------

export function useUpdateAutoReload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AutoReloadConfig) => {
      // apiClient returns { success:false } instead of throwing — throw so the
      // .mutate() onError callback actually fires on a failed save.
      const res = await paymentApi.updateAutoReloadStatus(data);
      if (!res.success) {
        throw new Error(res.error ?? "Failed to update auto-reload");
      }
      return res.data;
    },
    onSuccess: () => {
      // Auto-reload config can trigger downstream balance webhooks — invalidate the full payment domain to keep balance + sources in sync.
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
    // error handled at call site (.mutate() onError callback) — onError would double-toast
  });
}

// -- Tax Information ------------------------------------------------

/**
 * Update the tax information on the current company.
 *
 * Server controller (`updateTaxInfo`) reads `req.body.data` — the value MUST
 * be wrapped: `{ data: { taxType, taxNumber, description } }`. Sending the
 * raw object causes the server to silently store `undefined` fields.
 */
export function useUpdateTaxInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TaxInfo) => {
      const res = await apiClient.put<{ status: string }>("/user/tax", {
        data,
      });
      if (!res.success)
        throw new Error(res.error ?? "Failed to save tax information");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.taxInfo() });
    },
  });
}

// -- Billing Addresses ----------------------------------------------

/**
 * Server billing-address surface is array-replace via `PUT /user/address`
 * with `Joi.array().items(...)` body (see `updateAddressBody` validations:76-90).
 * Single-address ops (create / update / toggle-default) read the current list,
 * mutate the relevant entry, and PUT the whole array back.
 *
 * Delete uses `DELETE /user/address/:addressId`.
 */

/** Strip `id` before sending to the server (server assigns its own `_id`). */
function toServerPayload(
  addr: BillingAddress | Omit<BillingAddress, "id">,
): BillingAddressPayload {
  return {
    firstName: addr.firstName,
    lastName: addr.lastName,
    address1: addr.address1,
    address2: addr.address2 || null,
    city: addr.city,
    state: addr.state || null,
    country: addr.country,
    zipCode: addr.zipCode,
    isDefault: addr.isDefault,
  };
}

async function putAddressArray(addresses: BillingAddressPayload[]) {
  const res = await apiClient.put<{ status: string }>(
    "/user/address",
    addresses,
  );
  if (!res.success) throw new Error(res.error ?? "Failed to save address");
  return res;
}

export function useCreateBillingAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      newAddress,
      existing,
    }: {
      newAddress: Omit<BillingAddress, "id">;
      existing: BillingAddress[];
    }) => {
      const payload = [
        ...existing.map(toServerPayload),
        toServerPayload(newAddress),
      ];
      // If the new entry is default, clear the flag on every other entry.
      if (newAddress.isDefault) {
        payload.forEach((p, i) => {
          if (i < payload.length - 1) p.isDefault = false;
        });
      }
      return putAddressArray(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentKeys.billingAddresses(),
      });
    },
  });
}

export function useUpdateBillingAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      patch,
      existing,
    }: {
      id: string;
      patch: Partial<Omit<BillingAddress, "id">>;
      existing: BillingAddress[];
    }) => {
      const payload = existing.map((addr) => {
        const merged = addr.id === id ? { ...addr, ...patch } : addr;
        return toServerPayload(merged);
      });
      // Enforce single default if the patch turned this entry into the default.
      if (patch.isDefault) {
        const targetIdx = existing.findIndex((a) => a.id === id);
        payload.forEach((p, i) => {
          if (i !== targetIdx) p.isDefault = false;
        });
      }
      return putAddressArray(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentKeys.billingAddresses(),
      });
    },
  });
}

export function useDeleteBillingAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addressId: string) => {
      const res = await apiClient.delete<{ status: string }>(
        `/user/address/${addressId}`,
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to delete address");
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentKeys.billingAddresses(),
      });
    },
  });
}

// -- Coupon Mutations -----------------------------------------------

export function useVerifyCouponCode() {
  const toast = useToast();

  return useMutation({
    mutationFn: (data: { code: string }) => paymentApi.verifyCouponCode(data),
    onError: (err) => {
      toastError(toast, "subscription", "verify_coupon_fail", "Something went wrong. Please try again.", { error_reason: String(err).slice(0, 200) });
    },
  });
}

// -- Paddle Source Add (PayPal-via-Paddle) -------------------------

/**
 * Initiates a Paddle PayPal add-source flow:
 *   1. POSTs /payment/addPaddle → server returns { transactionId }
 *   2. Opens the Paddle checkout overlay with that transactionId
 *
 * The mutation resolves AS SOON AS the overlay opens — NOT when the user
 * completes the PayPal handoff. The actual payment-method-saved signal arrives
 * via the Paddle SDK event callback (see paddle.ts:initializePaddle), which
 * dispatches a `"paddle:event"` window CustomEvent that the dialog component
 * (add-source.tsx) listens to for `checkout.completed` / `checkout.closed` /
 * `checkout.error` reactions and final cache invalidation.
 */
export function useAddPaddleSource() {
  const { openCheckout } = usePaddle();
  return useMutation({
    mutationFn: async () => {
      const res = await paymentApi.addPaddlePayment();
      if (!res.success || !res.data?.transactionId) {
        throw new Error(res.error ?? "Failed to start Paddle checkout");
      }
      openCheckout(res.data.transactionId, undefined, {
        refreshSourcesOnComplete: true,
      });
      return { transactionId: res.data.transactionId };
    },
  });
}
